import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext();

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const getAuthHeader = useCallback(() => {
    const token = localStorage.getItem('community_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const fetchUser = useCallback(async () => {
    const token = localStorage.getItem('community_token');
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const response = await axios.get(`${API_URL}/api/community/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const userData = response.data.user;
      setUser(userData);
      setIsAuthenticated(true);
    } catch (error) {
      console.error('Session restore failed:', error);
      localStorage.removeItem('community_token');
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  // Auto-refresh user data every 2 minutes
  useEffect(() => {
    if (!isAuthenticated) return;
    const interval = setInterval(fetchUser, 120000);
    return () => clearInterval(interval);
  }, [isAuthenticated, fetchUser]);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API_URL}/api/community/login`, {
        email,
        password,
      });
      const { access_token, user: userData } = response.data;
      localStorage.setItem('community_token', access_token);
      setUser(userData);
      setIsAuthenticated(true);
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || 'Login fehlgeschlagen';
      return { success: false, error: message };
    }
  };

  const register = async (formData) => {
    try {
      const response = await axios.post(`${API_URL}/api/community/register`, formData);
      return { success: true, message: response.data.message };
    } catch (error) {
      const detail = error.response?.data?.detail;
      let message = 'Registrierung fehlgeschlagen';
      if (typeof detail === 'string') {
        message = detail;
      } else if (Array.isArray(detail)) {
        message = detail.map((d) => {
          let msg = d.msg || d.message || JSON.stringify(d);
          // Strip "Value error, " prefix from Pydantic validation errors
          msg = msg.replace(/^Value error,\s*/i, '');
          return msg;
        }).join('. ');
      }
      return { success: false, error: message };
    }
  };

  const logout = () => {
    localStorage.removeItem('community_token');
    setUser(null);
    setIsAuthenticated(false);
  };

  const isAdmin = user?.role === 'admin';
  const isCoach = user?.role === 'coach';
  const isSponsor = user?.role === 'sponsor';
  const isAdminOrCoach = isAdmin || isCoach;

  const value = {
    user,
    login,
    register,
    logout,
    refreshUser: fetchUser,
    isAuthenticated,
    loading,
    isAdmin,
    isCoach,
    isSponsor,
    isAdminOrCoach,
    getAuthHeader,
    API_URL,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
